import type Phaser from 'phaser';
import { gameAudio } from '../audio/audio';
import { haptics } from '../../platform/haptics';
import { saves } from '../records';
import { label, UI } from './text';
import { drawCoin } from './widgets';

/**
 * Menu motion (docs/ART_DIRECTION.md, motion guide): entrances and celebrations shared by the
 * menu, garage and shop. Everything here respects reduced motion.
 */

/** Reduced motion is on (the "screen shake" setting): skip movement, keep the information. */
export function reducedMotion(): boolean {
  return saves().save.settings.reducedShake;
}

type Movable = Phaser.GameObjects.GameObject &
  Phaser.GameObjects.Components.Transform &
  Partial<Phaser.GameObjects.Components.Alpha>;

/**
 * Slides everything drawn since `mark` (an index into the scene's display list) in from an offset,
 * fading in. Positions stay whole pixels. Usage: `const mark = scene.children.length`, draw, then
 * `arriveSince(scene, mark, ...)`.
 */
export function arriveSince(
  scene: Phaser.Scene,
  mark: number,
  dx: number,
  dy: number,
  delay: number,
  duration = 260,
  ease = 'Back.easeOut',
): void {
  if (reducedMotion()) return;
  for (const obj of scene.children.list.slice(mark)) {
    const o = obj as Movable;
    const x = o.x;
    const y = o.y;
    o.setPosition(x + dx, y + dy);
    o.setAlpha?.(0);
    const k = { t: 0 };
    scene.tweens.add({
      targets: k,
      t: 1,
      delay,
      duration,
      ease,
      onUpdate: () => {
        o.setPosition(Math.round(x + dx * (1 - k.t)), Math.round(y + dy * (1 - k.t)));
        o.setAlpha?.(Math.min(1, k.t * 2));
      },
      onComplete: () => o.setPosition(x, y),
    });
  }
}

export interface PurchaseFx {
  /** The wallet amount (right-aligned label), which counts down from `from` to `to`. */
  wallet: Phaser.GameObjects.BitmapText;
  from: number;
  to: number;
  /** Where the bought thing is shown (the showcase picture's center). */
  target: { x: number; y: number };
  /** Stamp text, e.g. "YOURS!" or "UPGRADED!". */
  stamp: string;
}

/**
 * A purchase lands: coins fly from the wallet into the item while the wallet counts down, then a
 * burst of sparkles and a stamp. With reduced motion the wallet just updates and the stamp shows.
 */
export function celebratePurchase(scene: Phaser.Scene, fx: PurchaseFx): void {
  const { wallet, from, to, target } = fx;
  const spent = Math.max(0, from - to);
  haptics.tap();
  if (reducedMotion()) {
    stamp(scene, fx.stamp, target, false);
    gameAudio.play('coin', 1.2);
    return;
  }
  const flight = 420;
  const count = Math.min(10, Math.max(3, Math.ceil(spent / 15)));
  const gap = 55;
  const sx = Math.round(wallet.x - wallet.width - 7);
  const sy = Math.round(wallet.y + 3);
  wallet.setText(String(from));
  const roll = { v: from };
  scene.tweens.add({
    targets: roll,
    v: to,
    duration: gap * count,
    onUpdate: () => wallet.setText(String(Math.round(roll.v))),
    onComplete: () => wallet.setText(String(to)),
  });
  for (let i = 0; i < count; i++) {
    const coin = scene.add.graphics().setDepth(50);
    drawCoin(coin, -3, -3);
    coin.setPosition(sx, sy);
    const lift = 20 + (i % 3) * 8;
    const k = { t: 0 };
    scene.tweens.add({
      targets: k,
      t: 1,
      delay: i * gap,
      duration: flight,
      ease: 'Sine.easeIn',
      onStart: () => {
        if (i % 2 === 0) gameAudio.play('coin', 0.9 + i * 0.04);
      },
      onUpdate: () => {
        // A little arc: up and over, then down into the item.
        const x = sx + (target.x - sx) * k.t;
        const y = sy + (target.y - sy) * k.t - Math.sin(k.t * Math.PI) * lift;
        coin.setPosition(Math.round(x), Math.round(y));
      },
      onComplete: () => coin.destroy(),
    });
  }
  scene.time.delayedCall(gap * (count - 1) + flight, () => {
    burst(scene, target);
    stamp(scene, fx.stamp, target, true);
    gameAudio.play('coin', 1.4);
    haptics.impact();
  });
}

/** Sparkles flying out from a point. */
function burst(scene: Phaser.Scene, at: { x: number; y: number }): void {
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const dist = 16 + (i % 3) * 7;
    const s = scene.add.graphics().setDepth(50).setPosition(at.x, at.y);
    s.fillStyle(i % 2 === 0 ? 0xffffff : UI.accent).fillRect(-1, -1, 2, 2);
    scene.tweens.add({
      targets: s,
      x: at.x + Math.cos(a) * dist,
      y: at.y + Math.sin(a) * dist,
      alpha: 0,
      duration: 480,
      ease: 'Quad.easeOut',
      onComplete: () => s.destroy(),
    });
  }
}

/**
 * A stamp over the item, like a sticker: a navy plate with a yellow rim (readable over any art).
 * Drops in with a bounce, holds, fades.
 */
function stamp(
  scene: Phaser.Scene,
  text: string,
  at: { x: number; y: number },
  move: boolean,
): void {
  const t = label(scene, 0, 0, text, 2, UI.accent).setDropShadow(0, 0, 0, 0);
  const w = Math.round(t.width) + 10;
  const h = 7 * 2 + 8;
  const g = scene.add.graphics();
  g.fillStyle(UI.shadow).fillRect(1, 2, w, h);
  g.fillStyle(UI.panel).fillRect(0, 0, w, h);
  g.lineStyle(1, UI.accent).strokeRect(0.5, 0.5, w - 1, h - 1);
  t.setPosition(5, 4);
  // Centred on the target, but kept on screen (a target near the edge would push it off).
  const screenW = scene.scale.gameSize.width;
  const x = Math.round(Math.max(4, Math.min(screenW - w - 4, at.x - w / 2)));
  const y = Math.round(at.y - h / 2);
  const plate = scene.add.container(x, y, [g, t]).setDepth(51);
  if (move) {
    plate.setY(y - 12).setAlpha(0);
    scene.tweens.add({ targets: plate, y, alpha: 1, duration: 320, ease: 'Bounce.easeOut' });
  }
  scene.tweens.add({
    targets: plate,
    alpha: 0,
    delay: 1300,
    duration: 400,
    onComplete: () => plate.destroy(),
  });
}
